import type { ReactNode } from "react";
import { ChevronDownIcon } from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { planTabs } from "@/lib/tabs";
import { cn } from "@/lib/utils";
import type { TabEntry } from "@/lib/tabs";

interface ModuleTabsProps {
  tabs: TabEntry[];
  activeTabKey: string | null;
  onOpenTab: (tabKey: string) => void;
  /**
   * Pass the available height down to the panel instead of sizing to it. Set
   * for a DOCKED surface, whose frame fills the viewport.
   */
  fill?: boolean;
  /**
   * Live counts a native module supplies for its tabs (registry
   * `useNativeModuleTabHints`), shown after the tab's name: "Open hand-offs &
   * alerts 4". A tab with no count, or a count of 0, shows its name alone.
   */
  counts?: Record<string, number>;
  /** The active tab's panel. Rendered inside the Radix panel so aria wiring is free. */
  children?: ReactNode;
}

/**
 * The module feature tab bar — shell-owned and non-optional (I-7, BR-13).
 *
 * A module declares its tabs at registration; the shell renders them. That is
 * what makes tab behaviour, keyboard handling, deep-linking and permission
 * filtering built once rather than eight times badly (REQ-PORTAL-023).
 *
 * Built on Radix Tabs (via the shadcn primitive), so arrow-key navigation,
 * roving tabindex and the `tab`/`tabpanel` relationship come from the library
 * rather than from a hand-rolled tablist — Tab moves INTO the panel, so a
 * keyboard user never traverses every tab to reach content (UX v0.3 §7).
 *
 * **Grouping replaced the "More" overflow — Damien, 2026-08-26.** UX S4b
 * specified six visible tabs and the rest behind one "More ▾". That was ruled a
 * UX error: "More" is an unnamed label, so a person cannot tell what it holds
 * and cannot predict where a feature lives. A module now declares a `tab_group`
 * per tab and the bar renders one row of NAMED group dropdowns plus any
 * ungrouped tabs. S4b is recorded as overridden, not silently dropped
 * (`src/lib/tabs.ts` carries the full note).
 *
 * Three rules are structural here rather than advisory:
 *
 * * **Never wraps. Grouping keeps the row to one line — scrolling is the
 *   last resort, not the plan.** A wrapped row reads as two different
 *   navigations, so wrapping stays banned. The original rule also said "never
 *   silently scrolls", and the row indeed had no `overflow-x-auto` — but it
 *   had no other overflow handling either, and for a docked module the parent
 *   chain now resolves to `overflow-hidden`. So a row that outgrew its width
 *   did not scroll: the tabs past the edge became UNREACHABLE, with no
 *   affordance and no escape hatch (UX audit, 2026-08-27). That is a strictly
 *   worse outcome than a scrollbar, and it silently breaks the third rule
 *   below. `overflow-x-auto` + `min-w-max` is therefore a safety net beneath
 *   grouping, not a substitute for it: if a module ever needs to scroll, its
 *   `tab_group`s are wrong and that is the thing to fix.
 * * **The active tab is always visible** — as its own tab, or as the label of
 *   the group it sits in. That is the S4b guarantee that survives the override:
 *   a person can always see where they are.
 * * **A tab is never removed for a data problem.** A disappearing tab reads as
 *   "this feature was taken away". Only permission filtering removes one, and
 *   that happens upstream in the registry response — never here.
 */
export function ModuleTabs({
  tabs,
  activeTabKey,
  onOpenTab,
  fill = false,
  counts,
  children,
}: ModuleTabsProps) {
  const plan = planTabs(tabs, activeTabKey);

  // UX S4b edge: a single-tab module IS its own view. No bar, and the content
  // still renders — the panel is not conditional on the chrome.
  if (!plan.render) return <>{children}</>;

  return (
    <Tabs
      value={activeTabKey ?? undefined}
      onValueChange={onOpenTab}
      className={fill ? "flex min-h-0 flex-1 flex-col" : undefined}
    >
      {/* The bar keeps the shell gutter even when the frame below it is
          flush, so the tabs line up with the top bar rather than jamming
          against the window edge. */}
      <div
        // overflow-x-auto alone turns overflow-y to auto too, and the line tab's
        // underline (drawn 5px below the trigger) then showed as a stray vertical
        // scrollbar on every module (Damien, 2026-09-28). Clip y; draw the underline inside.
        className={`flex items-end overflow-x-auto overflow-y-hidden border-b border-ks-grey-3${fill ? " px-8" : ""}`}
      >
        <TabsList
          variant="line"
          aria-label="Module features"
          className="h-auto min-w-max shrink-0 p-0"
        >
          {plan.items.map((item) =>
            item.kind === "tab" ? (
              <TabsTrigger
                key={item.tab.tab_key}
                value={item.tab.tab_key}
                // Active is marked by the underline AND the label weight — never
                // colour alone (UX v0.3 §7 accessibility).
                className="min-h-11 px-3 text-ks-ink data-active:font-bold after:bottom-0!"
                // Switching tabs changes nothing, so it is navigation, not an
                // edit. The bar renders INSIDE PermissionGate, whose `view`
                // wrapper sets pointer-events:none on every unmarked <button>;
                // without this marker every tab was inert for a view-only
                // operator — read-only degraded to no-access, the exact outcome
                // the gate says the system does not do (bug 3d20402f).
                data-nonmutating
              >
                {item.tab.display_name}
                <TabCount n={counts?.[item.tab.tab_key]} />
              </TabsTrigger>
            ) : (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger
                  // The group carries the active tab's NAME when the active tab
                  // is inside it, so where you are is never hidden behind a
                  // group label, and the weight marks it selected the same way
                  // a plain tab is marked.
                  // The underline is the same signal a plain active
                  // TabsTrigger carries. Without it the row spoke two visual
                  // languages — an underline if your tab happened to be
                  // ungrouped, a bold label if it happened to be grouped — so
                  // "where am I" was answered differently depending on a
                  // registration detail the person cannot see.
                  className={
                    "flex min-h-11 items-center gap-1 border-b-2 px-5 text-sm text-ks-ink" +
                    (item.activeTab ? " border-ks-purple font-bold" : " border-transparent")
                  }
                  aria-label={`${item.label} (${item.tabs.length} features)`}
                  // Same exemption as the plain TabsTrigger above: opening a
                  // group to pick a feature is navigation (bug 3d20402f).
                  data-nonmutating
                >
                  {item.activeTab
                    ? `${item.label} · ${item.activeTab.display_name}`
                    : item.label}
                  <ChevronDownIcon className="size-4" aria-hidden />
                </DropdownMenuTrigger>
                {/* The menu used to take the trigger's width, so "Analysis" opened
                    a strip narrower than its own feature names (Ioan, 30 Oct). */}
                <DropdownMenuContent align="start" className="w-auto min-w-60 p-1.5">
                  {item.tabs.map((tab) => (
                    <DropdownMenuItem
                      key={tab.tab_key}
                      onSelect={() => onOpenTab(tab.tab_key)}
                      className={cn("px-3 py-2 text-sm", tab.tab_key === activeTabKey && "font-bold")}
                    >
                      {tab.display_name}
                      <TabCount n={counts?.[tab.tab_key]} />
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ),
          )}
        </TabsList>
      </div>

      {activeTabKey && (
        <TabsContent value={activeTabKey} className={fill ? "flex min-h-0 flex-1 flex-col" : "pt-2"}>
          {children}
        </TabsContent>
      )}
    </Tabs>
  );
}

/** A tab's live count: a small pill after its name, absent at 0 so an empty queue reads as quiet. */
function TabCount({ n }: { n: number | undefined }) {
  if (!n) return null;
  return (
    <Badge className="ml-1.5" aria-label={`${n} waiting`}>
      {n}
    </Badge>
  );
}

/**
 * The tab bar's loading state.
 *
 * It renders BEFORE content on purpose: the person sees what the module does
 * while it loads, and can pick a different tab mid-load (UX S4b loading).
 */
export function ModuleTabsSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="flex items-end border-b border-ks-grey-3">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="flex min-h-11 items-center px-3 py-2">
          <Skeleton className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}
